import React, { useState } from "react";
import { FormValue } from "../../../redux/interfaces/folder.types";
import {
  TextField,
  SelectField,
  DateField,
  RadioField,
  CheckboxField,
  TextareaField,
  CurrencyField,
  FormFieldWrapper,
  FormSection,
} from "./FormField";
import { getFieldWidth } from "./fieldLayoutConfig";
import { countries } from "../../../constants/countries";
import { isEnabledSearchInput } from "../../../utils/vault/isEnabledSearchInput";
import { getFieldCaseStyle } from "../../../utils/vault/fieldCaseStyle";
import { getMaxAllowedChars } from "../../../utils/vault/maxAllowedChars";
import { Input } from "../../ui/input";
import { Button } from "../../ui/button";

// Mobile input helpers — determines inputMode and autoComplete from field type/key
const getInputModeForField = (
  fieldType: string,
  fieldKey: string
): React.HTMLAttributes<HTMLInputElement>['inputMode'] | undefined => {
  if (fieldType === 'email') return 'email';
  if (fieldType === 'number') return 'tel';
  if (fieldKey.includes('phone') || fieldKey.includes('cellphone')) return 'tel';
  if (fieldKey.includes('postal_code')) return 'numeric';
  return undefined;
};

const getAutoCompleteForField = (fieldKey: string): string | undefined => {
  if (fieldKey.includes('email')) return 'email';
  if (fieldKey.includes('phone') || fieldKey.includes('cellphone')) return 'tel';
  if (fieldKey.includes('postal_code')) return 'postal-code';
  return undefined;
};

interface DynamicFormBuilderProps {
  formItem: FormValue;
  value: any;
  onChange: (key: string, value: any) => void;
  onBlur?: (key: string) => void;
  requiredFields: Record<string, boolean>;
  displayFields: Record<string, "block" | "hidden">;
  labelFields: Record<string, string>;
  disabled?: boolean;
  errors?: Record<string, string>;
  /** Field keys with an async validation in flight (e.g. email DNS check) */
  validatingFields?: Record<string, boolean>;
  /** Field keys that are locked (pre-filled from verification and non-editable) */
  lockedFields?: string[];
  /** Dynamic options from Redux (e.g., colonias from postal code lookup) */
  dynamicOptions?: Record<string, any>;
  /** Tracks which select fields have "otro" selected, showing an additional text input */
  otherValueFields?: Record<string, boolean>;
}

export const DynamicFormBuilder: React.FC<DynamicFormBuilderProps> = ({
  formItem,
  value,
  onChange,
  onBlur,
  requiredFields,
  displayFields,
  labelFields,
  disabled = false,
  errors = {},
  validatingFields = {},
  lockedFields = [],
  dynamicOptions = {},
  otherValueFields = {},
}) => {
  const { key, type, placeholder, tooltip, options, separator } = formItem;

  const isRequired = requiredFields[key] ?? formItem.required;
  const isHidden = displayFields[key] === "hidden";
  const isLocked = lockedFields.includes(key);
  const label = labelFields[key] ?? formItem.label;
  const error = errors[key];
  const fieldWidth = getFieldWidth(key);
  const caseStyle = getFieldCaseStyle(key);
  const maxCharsConfig = getMaxAllowedChars(key);
  // Fall back to the backend cap (MAX_VALUE_LENGTH = 255) so fields without
  // a maxChars config can't type text the server rejects as too long.
  const maxLength = maxCharsConfig?.max ?? 255;
  const inputMode = getInputModeForField(type, key);
  const autoComplete = getAutoCompleteForField(key);

  const [otherFieldValue, setOtherFieldValue] = useState("");
  const isOtherSelected = otherValueFields[key] ?? false;

  const handleChange = (newValue: any) => {
    // Prevent changes to locked fields
    if (isLocked) return;
    onChange(key, newValue);
  };

  const handleAddOtherOption = () => {
    const trimmed = otherFieldValue.trim();
    if (!trimmed) return;
    onChange(key, trimmed);
    setOtherFieldValue("");
  };

  // Render section header if separator exists
  const renderSeparator = () => {
    if (separator && !isHidden) {
      return <FormSection title={separator} />;
    }
    return null;
  };

  const handleBlur = () => onBlur?.(key);

  const commonProps = {
    name: key,
    label,
    value: value ?? "",
    onChange: handleChange,
    onBlur: handleBlur,
    placeholder,
    required: isRequired,
    disabled: disabled || isLocked,
    error,
    tooltip,
    locked: isLocked,
  };

  switch (type) {
    case "input":
      return (
        <>
          {renderSeparator()}
          <FormFieldWrapper width={fieldWidth} hidden={isHidden} fieldKey={key}>
            <TextField {...commonProps} type="text" maxLength={maxLength} caseStyle={caseStyle} inputMode={inputMode} autoComplete={autoComplete} />
          </FormFieldWrapper>
        </>
      );

    case "email":
      return (
        <>
          {renderSeparator()}
          <FormFieldWrapper width={fieldWidth} hidden={isHidden} fieldKey={key}>
            <TextField
              {...commonProps}
              type="email"
              maxLength={maxLength}
              caseStyle={caseStyle}
              inputMode="email"
              autoComplete="email"
              validating={validatingFields[key]}
            />
          </FormFieldWrapper>
        </>
      );

    case "number":
      return (
        <>
          {renderSeparator()}
          <FormFieldWrapper width={fieldWidth} hidden={isHidden} fieldKey={key}>
            <TextField {...commonProps} type="tel" maxLength={maxLength} caseStyle={caseStyle} inputMode="tel" />
          </FormFieldWrapper>
        </>
      );

    case "currency":
      return (
        <>
          {renderSeparator()}
          <FormFieldWrapper width={fieldWidth} hidden={isHidden} fieldKey={key}>
            <CurrencyField {...commonProps} />
          </FormFieldWrapper>
        </>
      );

    case "select": {
      // If the field is prefilled (from OCR) and has a value, render as a disabled text input
      // instead of a select. The OCR value may not match any select option (e.g., "JALISCO" vs "Jalisco")
      // or options may not be loaded yet (e.g., colonias require postal code lookup).
      if (formItem.prefilled && value) {
        return (
          <>
            {renderSeparator()}
            <FormFieldWrapper width={fieldWidth} hidden={isHidden} fieldKey={key}>
              <TextField {...commonProps} type="text" />
            </FormFieldWrapper>
          </>
        );
      }

      // Use dynamic options from Redux (e.g., colonias from postal code lookup) if available
      const dynamicOpts = dynamicOptions[key];
      const resolvedOptions = dynamicOpts
        ? dynamicOpts.map((opt: string) => ({ label: opt, value: opt }))
        : options || [];
      const enabledSearch = isEnabledSearchInput(key);
      const allowAdd = key.includes("state") || key.includes("neighborhood");
      return (
        <>
          {renderSeparator()}
          <FormFieldWrapper width={fieldWidth} hidden={isHidden} fieldKey={key}>
            <SelectField
              {...commonProps}
              options={resolvedOptions}
              searchable={enabledSearch}
              allowAddItem={allowAdd}
            />
            {isOtherSelected && (
              <div className="mt-2 flex gap-2">
                <Input
                  placeholder="Introduce una nueva opción"
                  value={otherFieldValue}
                  onChange={(e) => setOtherFieldValue(e.target.value)}
                  disabled={disabled}
                  maxLength={maxLength}
                  className="flex-1"
                />
                <Button
                  type="button"
                  variant="default"
                  size="sm"
                  onClick={handleAddOtherOption}
                  disabled={disabled || !otherFieldValue.trim()}
                >
                  Elegir
                </Button>
              </div>
            )}
          </FormFieldWrapper>
        </>
      );
    }

    case "country": {
      const countryOptions = countries.map((c) => ({
        label: `${c.emoji} ${c.name}`,
        value: c.name,
      }));
      return (
        <>
          {renderSeparator()}
          <FormFieldWrapper width={fieldWidth} hidden={isHidden} fieldKey={key}>
            <SelectField {...commonProps} options={countryOptions} searchable />
          </FormFieldWrapper>
        </>
      );
    }

    case "date":
      return (
        <>
          {renderSeparator()}
          <FormFieldWrapper width={fieldWidth} hidden={isHidden} fieldKey={key}>
            <DateField {...commonProps} />
          </FormFieldWrapper>
        </>
      );

    case "radio":
      return (
        <>
          {renderSeparator()}
          <FormFieldWrapper width={fieldWidth} hidden={isHidden} fieldKey={key}>
            <RadioField {...commonProps} options={options || []} />
          </FormFieldWrapper>
        </>
      );

    case "checkbox":
      return (
        <>
          {renderSeparator()}
          <FormFieldWrapper width={fieldWidth} hidden={isHidden} fieldKey={key}>
            <CheckboxField
              name={key}
              label={label}
              checked={!!value}
              onChange={handleChange}
              required={isRequired}
              disabled={disabled || isLocked}
              error={error}
              locked={isLocked}
            />
          </FormFieldWrapper>
        </>
      );

    case "textarea":
      return (
        <>
          {renderSeparator()}
          <FormFieldWrapper width="full" hidden={isHidden} fieldKey={key}>
            <TextareaField {...commonProps} />
          </FormFieldWrapper>
        </>
      );

    default:
      return null;
  }
};

export default DynamicFormBuilder;
